import './styles/graphic.scss';
import * as d3 from 'd3';

const CURRENT_YEAR = new Date().getFullYear();
const PREV_YEAR = CURRENT_YEAR - 1;

// Color scheme
const COLOR_CURRENT = '#cc0000'; // CNN red — current year
const COLOR_PREV = '#595959'; // dark gray — previous year
const COLOR_HIST = '#cbcbcb'; // light gray — all earlier years
const COLOR_HIST_LABEL = '#888888'; // darker gray — historical range annotation

// Animation runs once per page load (not on every resize redraw)
const animState = { done: false };

export default function (props, container, isMobile) {
  const {
    width, height, margins, initialWidth, initialHeight, data,
  } = props;

  if (!data || !data.length) return;

  // ── Scales ────────────────────────────────────────────────────────────────

  // X: calendar day-of-year, all mapped onto a single reference year (2020 = leap year)
  const xScale = d3.scaleTime()
    .domain([new Date(2020, 0, 1), new Date(2020, 11, 31)])
    .range([0, width]);

  // Y: acres burned
  const maxAcres = d3.max(data, series => d3.max(series.values, v => v.acres));
  const yScale = d3.scaleLinear()
    .domain([0, maxAcres])
    .range([height, 0])
    .nice();

  // ── Line generator ────────────────────────────────────────────────────────

  const lineFn = d3.line()
    .x(d => xScale(d.normalizedDate))
    .y(d => yScale(d.acres))
    .curve(d3.curveStepAfter);

  // ── SVG scaffold ──────────────────────────────────────────────────────────

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  const g = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // ── Chart label (HTML, above the SVG) ────────────────────────────────────

  container.insert('div', 'svg')
    .attr('class', 'chart-label')
    .style('font-size', '14px')
    .style('font-weight', '500')
    .style('font-family', '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Arial, sans-serif')
    .style('color', '#262626')
    .style('margin-bottom', '2px')
    .style('margin-top', '20px')
    .style('padding-left', '0')
    .text('Total acres burned, by week of the year');

  // ── Y axis + horizontal gridlines ─────────────────────────────────────────

  const yTickCount = isMobile ? 4 : 5;

  const yAxis = d3.axisLeft(yScale)
    .ticks(yTickCount)
    .tickFormat((d) => {
      if (d === 0) return '';
      if (d >= 1e6) return `${d3.format('.0f')(d / 1e6)}M`;
      return `${d3.format('.0f')(d / 1e3)}K`;
    })
    .tickSize(-width);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis)
    .call(axis => axis.select('.domain').remove())
    .selectAll('text')
    .style('fill', '#b1b1b1')
    .style('font-size', '12px')
    .style('font-family', '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Arial, sans-serif')
    .call((labels) => {
      labels
        .attr('x', 4)
        .attr('dy', '-0.4em')
        .attr('text-anchor', 'start');
    });

  // ── X axis ────────────────────────────────────────────────────────────────

  const xMonths = isMobile
    ? d3.timeMonth.every(2) // every other month on small screens
    : d3.timeMonth.every(1);

  const xAxis = d3.axisBottom(xScale)
    .ticks(xMonths)
    .tickFormat(d3.timeFormat('%b'))
    .tickSize(0)
    .tickPadding(8);

  const xAxisG = g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(0,${height})`)
    .call(xAxis)
    .call(axis => axis.select('.domain').remove());

  xAxisG.selectAll('text')
    .style('fill', '#b1b1b1')
    .style('font-size', '12px')
    .style('font-family', '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Arial, sans-serif');

  xAxisG.select('.tick:first-child text')
    .attr('text-anchor', 'start');

  // ── Historical range annotation ───────────────────────────────────────────

  const histYears = data
    .map(d => d.year)
    .filter(y => y !== CURRENT_YEAR && y !== PREV_YEAR)
    .sort((a, b) => a - b);

  // ── Lines ─────────────────────────────────────────────────────────────────

  // Track the current year end-label position for collision detection below
  let currentYearLabelY = null;

  // Draw order: historical first → prev year → current year on top
  const drawOrder = [...data].sort((a, b) => {
    const rank = y => (y === CURRENT_YEAR ? 2 : y === PREV_YEAR ? 1 : 0);
    return rank(a.year) - rank(b.year);
  });

  drawOrder.forEach(({ year, values }) => {
    const isCurrent = year === CURRENT_YEAR;
    const isPrev = year === PREV_YEAR;

    const stroke = isCurrent ? COLOR_CURRENT : isPrev ? COLOR_PREV : COLOR_HIST;
    const strokeWidth = isCurrent ? 2.5 : isPrev ? 1.5 : 1;
    const opacity = isCurrent ? 1 : isPrev ? 0.85 : 0.7;

    g.append('path')
      .datum(values)
      .attr('class', `line year-${year}`)
      .attr('fill', 'none')
      .attr('stroke', stroke)
      .attr('stroke-width', strokeWidth)
      .attr('opacity', opacity)
      .attr('d', lineFn);

    // End-of-line label for current and previous year only
    if (isCurrent || isPrev) {
      const last = values[values.length - 1];
      const dotX = xScale(last.normalizedDate);
      const dotY = yScale(last.acres);

      // small dot at the end of the line
      g.append('circle')
        .attr('class', `line-marker line-marker-${year}`)
        .attr('cx', dotX)
        .attr('cy', dotY)
        .attr('r', 3.5)
        .attr('fill', stroke);

      const labelAttrs = (sel) => {
        const nearRight = dotX > width - 20;
        return sel
          .attr('class', `line-label line-label-${year}`)
          .attr('x', nearRight ? width : dotX)
          .attr('y', dotY - 10)
          .attr('text-anchor', nearRight ? 'end' : 'middle')
          .attr('font-size', '12px')
          .attr('font-weight', isCurrent ? '700' : '500')
          .style('font-family', '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Arial, sans-serif')
          .text(year);
      };

      if (isCurrent) currentYearLabelY = dotY - 10;

      // halo: same text drawn behind with a wide white stroke
      g.append('text')
        .call(labelAttrs)
        .attr('fill', 'none')
        .attr('stroke', '#fefefe')
        .attr('stroke-width', 6)
        .attr('stroke-linejoin', 'round');

      // foreground text
      g.append('text')
        .call(labelAttrs)
        .attr('fill', stroke);
    }
  });

  // ── Annotations (drawn last so they sit above all lines) ──────────────────

  if (histYears.length) {
    const annotX = xScale(new Date(2020, 9, 1)); // ~Oct 1
    const MIN_CLEARANCE = 20; // px between annotation and 2026 label
    let annotY = yScale(7e6); // default: between 6M and 8M

    if (currentYearLabelY !== null) {
      const overlap = MIN_CLEARANCE - Math.abs(annotY - currentYearLabelY);
      if (overlap > 0) {
        // Shift annotation away from the 2026 label
        annotY = annotY < currentYearLabelY
          ? currentYearLabelY + MIN_CLEARANCE
          : currentYearLabelY - MIN_CLEARANCE;
        // Clamp within chart bounds
        annotY = Math.max(12, Math.min(height - 8, annotY));
      }
    }

    const annotText = `${histYears[0]}–${histYears[histYears.length - 1]}`;

    const annotAttrs = sel => sel
      .attr('class', 'hist-annotation')
      .attr('x', annotX)
      .attr('y', annotY)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .style('font-size', '12px')
      .style('font-family', '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Arial, sans-serif')
      .text(annotText);

    // halo
    g.append('text')
      .call(annotAttrs)
      .attr('fill', 'none')
      .attr('stroke', '#fefefe')
      .attr('stroke-width', 6)
      .attr('stroke-linejoin', 'round');

    // foreground
    g.append('text')
      .call(annotAttrs)
      .attr('fill', COLOR_HIST_LABEL)
      .style('fill', COLOR_HIST_LABEL);
  }

  // ── Scroll-triggered draw animation ──────────────────────────────────────

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!animState.done && !prefersReducedMotion) {
    const linePaths = g.selectAll('.line').nodes();
    const fadeEls = g.selectAll('.line-label, .line-marker, .hist-annotation').nodes();

    // Hide post-line elements until animation completes
    fadeEls.forEach(el => d3.select(el).attr('opacity', 0));

    // Pre-measure and set dasharray/dashoffset on every line path
    linePaths.forEach((path) => {
      if (typeof path.getTotalLength !== 'function') return;
      const len = path.getTotalLength();
      if (!len) return;
      d3.select(path)
        .attr('stroke-dasharray', len)
        .attr('stroke-dashoffset', len);
    });

    const STAGGER_MS = 40;
    const DURATION_MS = 1100;
    const FADE_MS = 600;

    function runAnimation() {
      animState.done = true;
      let maxEnd = 0;

      linePaths.forEach((path, i) => {
        if (typeof path.getTotalLength !== 'function') return;
        const len = path.getTotalLength();
        if (!len) return;
        const delay = i * STAGGER_MS;
        maxEnd = Math.max(maxEnd, delay + DURATION_MS);

        d3.select(path)
          .transition()
          .delay(delay)
          .duration(DURATION_MS)
          .ease(d3.easeQuadOut)
          .attr('stroke-dashoffset', 0);
      });

      // Fade in labels and markers after all lines finish drawing
      window.setTimeout(() => {
        fadeEls.forEach((el) => {
          const cls = d3.select(el).attr('class') || '';
          const yearMatch = cls.match(/year-(\d+)/) || cls.match(/line-(?:marker|label)-(\d+)/);
          const targetOpacity = yearMatch ? lineOpacity(+yearMatch[1]) : 1;
          d3.select(el).transition().duration(FADE_MS).attr('opacity', targetOpacity);
        });
      }, maxEnd + 50);
    }

    // Trigger when the SVG scrolls into view
    const svgNode = svg.node();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        window.requestAnimationFrame(() => window.requestAnimationFrame(runAnimation));
      });
    }, { threshold: 0.1 });

    observer.observe(svgNode);

    // Also handle pym embedded scroll detection (same approach as trump-approval)
    const { pymChild } = window;
    if (pymChild && typeof pymChild.onMessage === 'function') {
      pymChild.onMessage('viewport-iframe-position', (positionString) => {
        if (animState.done) return;
        const parts = positionString.split(' ').map(parseFloat);
        if (parts.length < 3 || parts.some(n => isNaN(n))) return;
        const [scrollTop,, iframeTop] = parts;
        const viewportHeight = parts[1];
        const rect = svgNode.getBoundingClientRect();
        const ratio = 1 - (rect.top + iframeTop - scrollTop) / viewportHeight;
        if (ratio > 0.1) {
          observer.disconnect();
          window.requestAnimationFrame(() => window.requestAnimationFrame(runAnimation));
        }
      });
      if (pymChild.parentUrl) pymChild.sendMessage('request-viewport-iframe-position');
    }
  }

  // ── Tooltip ───────────────────────────────────────────────────────────────

  // Flatten all points into a lookup array with their pixel coords
  const allPoints = [];
  data.forEach(({ year, values }) => {
    values.forEach((pt) => {
      allPoints.push({
        year,
        date: pt.date,
        acres: pt.acres,
        px: xScale(pt.normalizedDate),
        py: yScale(pt.acres),
      });
    });
  });

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function formatDate(d) {
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }

  function lineOpacity(year) {
    if (year === CURRENT_YEAR) return 1;
    if (year === PREV_YEAR) return 0.85;
    return 0.7;
  }

  // SVG tooltip group — two lines of halo text, no background box
  const tooltipGroup = g.append('g')
    .attr('class', 'tooltip-text')
    .style('display', 'none')
    .style('pointer-events', 'none');

  function haloText(parent, text, x, y, bold, color) {
    const apply = sel => sel
      .attr('x', x)
      .attr('y', y)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('font-weight', bold ? '700' : '400')
      .style('font-family', '"CNN Sans Display", "CNN Sans", "Helvetica Neue", Arial, sans-serif')
      .text(text);
    parent.append('text').call(apply)
      .attr('fill', 'none')
      .attr('stroke', '#fefefe')
      .attr('stroke-width', 5)
      .attr('stroke-linejoin', 'round');
    parent.append('text').call(apply)
      .attr('fill', color || '#262626');
  }

  // Highlight dot
  const highlight = g.append('circle')
    .attr('class', 'tooltip-dot')
    .attr('r', 4)
    .attr('fill', '#fff')
    .attr('stroke-width', 2)
    .style('display', 'none')
    .style('pointer-events', 'none');

  function restoreAllOpacity() {
    g.selectAll('.line, .line-marker, .line-label').each(function () {
      const cls = d3.select(this).attr('class') || '';
      const match = cls.match(/year-(\d+)/) || cls.match(/line-(?:marker|label)-(\d+)/);
      if (match) d3.select(this).attr('opacity', lineOpacity(+match[1]));
    });
    g.selectAll('.hist-annotation').attr('opacity', 1);
  }

  function getCoords() {
    const e = d3.event.touches ? d3.event.touches[0] : d3.event;
    const rect = d3.event.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  }

  function handlePointer() {
    const [mx, my] = getCoords();

    // Find nearest point — labelled years (2025/2026) get a distance bonus
    // so the cursor naturally follows them, while historical lines remain selectable
    let closest = null;
    let minDist = Infinity;
    allPoints.forEach((pt) => {
      const raw = Math.hypot(pt.px - mx, pt.py - my);
      const penalty = (pt.year === CURRENT_YEAR || pt.year === PREV_YEAR) ? 1 : 2.5;
      const dist = raw * penalty;
      if (dist < minDist) { minDist = dist; closest = pt; }
    });
    if (!closest) return;

    const ptColor = closest.year === CURRENT_YEAR ? COLOR_CURRENT
      : closest.year === PREV_YEAR ? COLOR_PREV
        : COLOR_HIST_LABEL;

    // Dim all lines only when hovering a labelled year (2025 or 2026)
    if (closest.year === CURRENT_YEAR || closest.year === PREV_YEAR) {
      g.selectAll('.line, .line-marker, .line-label, .hist-annotation').attr('opacity', 0.08);
      g.selectAll(`.line.year-${closest.year}, .line-marker-${closest.year}, .line-label-${closest.year}`)
        .attr('opacity', lineOpacity(closest.year));
    } else {
      // Restore all opacities when hovering a historical year
      restoreAllOpacity();
    }

    // Move highlight dot
    highlight
      .style('display', null)
      .attr('cx', closest.px)
      .attr('cy', closest.py)
      .attr('stroke', ptColor);

    // Build SVG text tooltip — above point by default, below if near top edge
    tooltipGroup.selectAll('*').remove();
    const lineH = 16;
    const aboveY1 = closest.py - lineH - 8; // date line
    const aboveY2 = closest.py - 8; // value line
    const belowY1 = closest.py + 16;
    const belowY2 = closest.py + 16 + lineH;
    const showAbove = aboveY1 > 4;
    const y1 = showAbove ? aboveY1 : belowY1;
    const y2 = showAbove ? aboveY2 : belowY2;

    // Clamp x so text doesn't overflow left/right
    const labelX = Math.max(30, Math.min(closest.px, width - 30));

    haloText(tooltipGroup, formatDate(closest.date), labelX, y1, true, ptColor);
    haloText(tooltipGroup, `${d3.format('.1f')(closest.acres / 1e6)}M acres`, labelX, y2, false, ptColor);
    tooltipGroup.style('display', null);
  }

  function handleLeave() {
    restoreAllOpacity();
    tooltipGroup.style('display', 'none');
    highlight.style('display', 'none');
  }

  // Transparent overlay rect captures all mouse/touch events
  g.append('rect')
    .attr('width', width)
    .attr('height', height)
    .attr('fill', 'none')
    .attr('pointer-events', 'all')
    .on('mousemove', handlePointer)
    .on('mouseleave', handleLeave)
    .on('touchmove.tooltip', () => { d3.event.preventDefault(); handlePointer(); })
    .on('touchend.tooltip', handleLeave);
}
